import { ApiError, createAction } from 'redux-api-middleware';
import { State as StoreState } from '../types/State';
import { Dispatch, ThunkAction } from '../types/Store';
import { HTTP_GET } from './ApiAction';
import { resetBonusActivities } from './BonusActivity';
import { resetBonusRewards } from './BonusShop';
import { resetBonusTimeline } from './BonusTimeline';

/* Action definition for loading both dashboards */
export const LOAD_DASHBOARD = 'barmer-bonus-shared/dashboard/all/LOAD' as const;
export const LOAD_DASHBOARD_SUCCESS = 'barmer-bonus-shared/dashboard/all/SUCCESS' as const;
export const LOAD_DASHBOARD_FAILURE = 'barmer-bonus-shared/dashboard/all/FAILURE' as const;

export const SET_ACTIVE_USER = 'barmer-bonus-shared/dashboard/all/SET_ACTIVE_USER' as const;

type LoadDashboard = { type: typeof LOAD_DASHBOARD; payload?: ApiError; error?: boolean };
type DashboardUpdate = { type: typeof LOAD_DASHBOARD_SUCCESS; payload: DashboardPayload };
type DashboardUpdateFailure = {
  type: typeof LOAD_DASHBOARD_FAILURE;
  payload?: ApiError;
  error?: boolean;
};

type SetActiveUser = { type: typeof SET_ACTIVE_USER; userId: string };

export type Action = LoadDashboard | DashboardUpdate | DashboardUpdateFailure | SetActiveUser;

export const DASHBOARD_LOADING = 'loading' as const;
export const DASHBOARD_LOADED = 'loaded' as const;
export const DASHBOARD_ERROR = 'error' as const;
export const DASHBOARD_UNKNOWN = 'unknown' as const;

export type LoadState =
  | typeof DASHBOARD_LOADING
  | typeof DASHBOARD_LOADED
  | typeof DASHBOARD_ERROR
  | typeof DASHBOARD_UNKNOWN;

export type BonusDashboardType = {
  registered: boolean;
  registrationDate: string;
  pendingPoints: number;
  points: number;
};

type DashboardPayload = {
  bonus: BonusDashboardType;
  additionalInsured: Array<SubProfileDashboard>;
  firstName: string;
  lastName: string;
  id: string;
  analyticsEnabled?: boolean | null;
  errorMonitoringEnabled?: boolean | null;
};

export type DashboardType = {
  bonus: BonusDashboardType;
};

export type SubProfileDashboard = {
  id: string;
  firstName: string;
  lastName: string;
  points: number;
  pendingPoints: number;
};

export type State = {
  initialized: boolean; // whether there has been at least one (1) successful loading attempt
  loading: LoadState;
  error?: ApiError;
  activeUserId: string;
  bonus: BonusDashboardType;
  additionalInsured: Array<SubProfileDashboard>;
  id: string;
  firstName: string;
  lastName: string;
  analyticsEnabled?: boolean | null;
  errorMonitoringEnabled?: boolean | null;
};

const initialState: State = {
  initialized: false,
  loading: DASHBOARD_UNKNOWN,
  error: undefined,
  activeUserId: '', // empty string means main account active.
  bonus: {
    registered: false, // possibly we could assume true after successful login
    registrationDate: '',
    pendingPoints: 0,
    points: 0,
  },
  firstName: '',
  lastName: '',
  id: '', // main user id, Hauptversicherter
  additionalInsured: [],
  analyticsEnabled: null,
  errorMonitoringEnabled: null,
};

function determineLoadingState(action: Action): LoadState {
  return !('error' in action) || action.error === undefined ? DASHBOARD_LOADING : DASHBOARD_ERROR;
}

function checkForError(action: Action): ApiError | undefined {
  return 'error' in action && action.error ? action.payload : undefined;
}

export default function reducer(state: State = initialState, action: Action): State {
  switch (action.type) {
    case LOAD_DASHBOARD:
      return {
        ...state,
        loading: determineLoadingState(action),
        error: checkForError(action),
      };

    case LOAD_DASHBOARD_SUCCESS:
      return {
        ...state,
        bonus: action.payload.bonus,
        firstName: action.payload.firstName,
        lastName: action.payload.lastName,
        id: action.payload.id,
        additionalInsured: action.payload.additionalInsured,
        loading: DASHBOARD_LOADED,
        error: undefined,
        initialized: true,
        analyticsEnabled: action.payload.analyticsEnabled,
        errorMonitoringEnabled: action.payload.errorMonitoringEnabled,
      };

    case LOAD_DASHBOARD_FAILURE:
      return {
        ...state,
        loading: DASHBOARD_ERROR,
        error: action.payload,
      };

    case SET_ACTIVE_USER:
      return {
        ...state,
        activeUserId: action.userId,
      };

    default:
      return state;
  }
}

export function isDashboardLoaded(globalState: StoreState): boolean {
  return (
    DASHBOARD_LOADED === globalState.dashboard.loading ||
    DASHBOARD_ERROR === globalState.dashboard.loading
  );
}

export function isDashboardLoading(globalState: StoreState): boolean {
  return globalState.dashboard.loading === DASHBOARD_LOADING;
}

export function isDashboardInitialized(globalState: StoreState): boolean {
  return globalState.dashboard.initialized;
}

export const loadDashboard = (refresh = false) =>
  createAction({
    endpoint: '/dashboard',
    types: [LOAD_DASHBOARD, LOAD_DASHBOARD_SUCCESS, LOAD_DASHBOARD_FAILURE],
    method: HTTP_GET,
    bailout: (state: StoreState) => {
      return (!refresh && isDashboardLoaded(state)) || isDashboardLoading(state);
    },
  });

export function setActiveUser(userId: string): SetActiveUser {
  return {
    type: SET_ACTIVE_USER,
    userId,
  };
}

export function switchActiveUser(userId: string): ThunkAction {
  return (dispatch: Dispatch) => {
    dispatch(setActiveUser(userId));
    dispatch(resetBonusTimeline());
    dispatch(resetBonusActivities());
    dispatch(resetBonusRewards());
  };
}

export const isMainInsuredActive = (globalState: StoreState): boolean => {
  return globalState.dashboard.activeUserId === '';
};
